import { useTranslation } from "react-i18next";
import editIcon from "../../../assets/images/icons/edit_white.svg";
import { GeneralButton, Loader, NoResult } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetData } from "../../../hooks";
import { IGetContactResponse } from "../../../interface/GeneralResponse";
import { TitleWithIcon } from "../../../Share";

import phoneIcon from "../../../assets/images/icons/phone_b.svg";
import telephoneIcon from "../../../assets/images/icons/telephone_icon.svg";
import emailIcon from "../../../assets/images/icons/mail.svg";
import websiteIcon from "../../../assets/images/icons/website.svg";

function ContactInfo() {
  const { t } = useTranslation();
  const { data, isPending, isLoading } = useGetData<IGetContactResponse>(
    EndPoints.GetContactInfo,
  );

  const contacts = data?.Data?.Contacts || [];

  const phoneNumbers = contacts.filter((contact) => contact.TypeId === 1);
  const telephoneNumbers = contacts.filter((contact) => contact.TypeId === 2);
  const emails = contacts.filter((contact) => contact.TypeId === 3);
  const websites = contacts.filter((contact) => contact.TypeId === 4);

  const isAllDataEmpty =
    phoneNumbers.length === 0 &&
    telephoneNumbers.length === 0 &&
    emails.length === 0 &&
    websites.length === 0;

  const getIconByType = (typeId: number) => {
    switch (typeId) {
      case 1:
        return phoneIcon;
      case 2:
        return telephoneIcon;
      case 3:
        return emailIcon;
      case 4:
        return websiteIcon;
      default:
        return phoneIcon;
    }
  };

const getDisplayValue = (contact: any) => {
  if (contact.TypeId === 1) {
    const countryCode = contact.Country?.Code || "";
    let value = contact.Value;

    // If value has exactly 10 digits and country code exists, remove first digit
    if (countryCode && value && /^\d{10}$/.test(value)) {
      value = value.slice(1);
    }

    if (countryCode && !value.startsWith(countryCode)) {
      return `${countryCode} ${value}`;
    }
    return value;
  }
  return contact.Value;
};

  return (
    <div className="box flex h-full w-full flex-col gap-2 overflow-y-auto">
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold">{t("contact_info")}</p>
        {!isAllDataEmpty && (
          <GeneralButton
            color="yellow"
            icon={editIcon}
            title={t("edit")}
            className="!px-3 !py-2"
            route="/profile/contact_info/edit"
          />
        )}
      </div>
      {isPending || isLoading ? (
        <Loader />
      ) : !isAllDataEmpty ? (
        <>
          {phoneNumbers.length > 0 && (
            <>
              {phoneNumbers.map((contact, index) => (
                <TitleWithIcon
                  key={`phone-${index}`}
                  icon={getIconByType(contact.TypeId)}
                  title={getDisplayValue(contact)}
                  titleDir="ltr"
                />
              ))}
            </>
          )}
          {telephoneNumbers.length > 0 && (
            <>
              {telephoneNumbers.map((contact, index) => (
                <TitleWithIcon
                  key={`telephone-${index}`}
                  icon={getIconByType(contact.TypeId)}
                  title={getDisplayValue(contact)}
                />
              ))}
            </>
          )}

          {emails.length > 0 && (
            <>
              {emails.map((contact, index) => (
                <TitleWithIcon
                  key={`email-${index}`}
                  icon={getIconByType(contact.TypeId)}
                  title={contact.Value}
                />
              ))}
            </>
          )}
          {websites.length > 0 && (
            <div className="flex flex-col gap-2">
              {websites.map((contact, index) => (
                <TitleWithIcon
                  key={`website-${index}`}
                  icon={getIconByType(contact.TypeId)}
                  title={contact.Value}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="flex w-full flex-col items-center justify-center gap-4 rounded-lg bg-InputFieldColor py-11">
          <NoResult isWithLogo />
          <GeneralButton
            type="button"
            title={t("add_contact_info")}
            route="/profile/contact_info/edit"
          />
        </div>
      )}
    </div>
  );
}

export default ContactInfo;
